/* eslint-disable @typescript-eslint/no-unsafe-return */

'use client';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { c as _c } from "react/compiler-runtime";
import { useDebouncedValue } from 'design-system/client';
import { isEqual } from 'lodash';
import { type RefObject, useMemo, useState } from 'react';
import type { BareFetcher, SWRConfiguration, SWRHook } from 'swr';
export function useBefore(before, key) {
  const [prevKey, setPrevKey] = useState(key);
  if (typeof before.current !== "function") {
    return;
  }
  if (prevKey === key) {
    return;
  }
  setPrevKey(key);
  before.current();
}
export function useAfter(after, isLoading, data) {
  const [prevData, setPrevData] = useState(data);
  if (typeof after.current !== "function") {
    return;
  }
  if (isLoading) {
    return;
  }
  const currentData = data;
  if (prevData === currentData) {
    return;
  }
  after.current(currentData);
  setPrevData(currentData);
}
const INITIAL_DATA = Symbol('INITIAL_DATA');
export function useSelectedData(selector, data, isLoading) {
  const $ = _c(5);
  const [prevRawData, setPrevRawData] = useState(data);
  const [prevData, setPrevData] = useState(INITIAL_DATA);
  if (isLoading) {
    return data;
  }
  if (!selector?.current) {
    return data;
  }
  if (prevRawData === data && prevData !== INITIAL_DATA) {
    return prevData;
  }
  setPrevRawData(data);
  let selected;
  let t0;
  if ($[0] !== data || $[1] !== prevData || $[2] !== selector) {
    selected = selector.current(data);
    t0 = isEqual(prevData, selected);
    $[0] = data;
    $[1] = prevData;
    $[2] = selector;
    $[3] = selected;
    $[4] = t0;
  } else {
    selected = $[3];
    t0 = $[4];
  }
  if (t0) {
    return prevData;
  }
  setPrevData(selected);
  return selected;
}
function getSWRMiddleware(before: RefObject<(() => void) | undefined>, after: RefObject<((data: any) => void) | undefined>, selector: RefObject<(data: any) => any> | undefined, debounceMs: number | undefined = 0) {
  function middleware(useSWRNext: SWRHook) {
    return function useWithMiddleware(key_: any, fetcher: BareFetcher<any> | null, config: SWRConfiguration) {
      const serializedKey = useDebouncedValue(JSON.stringify(key_), debounceMs);
      const key = useMemo(() => JSON.parse(serializedKey), [serializedKey]);
      useBefore(before, serializedKey);
      const {
        data,
        ...swr
      } = useSWRNext(key, fetcher, config);
      const selectedData = useSelectedData(selector, data, swr.isLoading);
      useAfter(after, swr.isLoading, selectedData);
      return {
        ...swr,
        data: selectedData
      };
    };
  }
  return middleware;
}
export default getSWRMiddleware;